import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { hasAnyPermission, hasPermission } from "../../utils/permissions";
import type {
  FinanceDepartment,
  FinanceVoucher,
  FinanceVoucherList,
} from "../../types";
import VoucherFormModal, { VoucherType } from "./VoucherFormModal";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const STATUS_TAG: Record<string, { color: string; label: string }> = {
  draft: { color: "default", label: "Draft" },
  pending_approval: { color: "processing", label: "Pending approval" },
  approved: { color: "success", label: "Approved" },
  rejected: { color: "error", label: "Rejected" },
  paid: { color: "success", label: "Paid" },
  posted: { color: "success", label: "Posted" },
  void: { color: "default", label: "Voided" },
};

export function StatusTag({ status }: { status: string }) {
  const meta = STATUS_TAG[status] || { color: "default", label: status };
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

const VouchersPage: React.FC = () => {
  const navigate = useNavigate();

  const canIncome = hasAnyPermission("finance:income");
  const canExpense = hasAnyPermission("finance:expense");

  // The sidebar "Income" / "Expenses" buttons land here via ?type=income|expense.
  // Read it (permission-aware) so each button opens the matching tab, keeping the
  // value in the URL as the source of truth. Falls back to the old default if absent.
  const [searchParams, setSearchParams] = useSearchParams();
  const tp = searchParams.get("type");
  const urlType: VoucherType | null =
    tp === "income" && canIncome
      ? "income"
      : tp === "expense" && canExpense
        ? "expense"
        : null;

  const [requested, setRequested] = useState<VoucherType | null>(urlType);
  const activeType: VoucherType = requested ?? (canIncome ? "income" : "expense");

  // Both sidebar buttons share the /finance/vouchers route, so React Router keeps
  // this component mounted when switching between them — sync `requested` when the
  // URL type changes (covers menu navigation and shared/bookmarked links).
  useEffect(() => {
    if (urlType) setRequested(urlType);
  }, [urlType]);

  const [status, setStatus] = useState<string | undefined>();
  const [departmentId, setDepartmentId] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<FinanceVoucherList | null>(null);
  const [departments, setDepartments] = useState<FinanceDepartment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeService.getVouchers({
        type: activeType,
        status,
        department_id: departmentId,
        from: range ? range[0].format("YYYY-MM-DD") : undefined,
        to: range ? range[1].format("YYYY-MM-DD") : undefined,
        q: q || undefined,
        page,
        limit: 20,
      });
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  }, [activeType, status, departmentId, range, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    financeService
      .getDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]));
  }, []);

  /** Run a workflow action and refresh; failures show the server's reason. */
  const runAction = async (label: string, action: () => Promise<any>) => {
    try {
      await action();
      message.success(label);
      await load();
    } catch (e: any) {
      message.error(e?.message || `${label} failed`);
    }
  };

  const canCreate = activeType === "income" ? canIncome : canExpense;

  const columns = useMemo(
    () => [
      {
        title: "Voucher",
        dataIndex: "voucher_no",
        key: "voucher_no",
        width: 150,
        render: (v: string, row: FinanceVoucher) => (
          <a onClick={() => navigate(`/finance/vouchers/${row.id}`)}>{v}</a>
        ),
      },
      {
        title: "Date",
        dataIndex: "business_date",
        key: "business_date",
        width: 110,
        render: (v: string) => dayjs(v).format("MMM D, YYYY"),
      },
      {
        title: "Department",
        dataIndex: "department_name",
        key: "department_name",
        width: 160,
        ellipsis: true,
      },
      {
        title: "Account",
        key: "account",
        width: 190,
        ellipsis: true,
        render: (_: any, row: FinanceVoucher) => (
          <Text code>{row.account_code}</Text>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
      },
      {
        title: "Party",
        dataIndex: "party",
        key: "party",
        width: 150,
        ellipsis: true,
        render: (v: string | null) => v || "—",
      },
      {
        title: "Amount",
        key: "amount",
        width: 140,
        align: "right" as const,
        render: (_: any, row: FinanceVoucher) => (
          <Text strong>{formatLkr(row.amount + row.tax)}</Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 150,
        render: (s: string, row: FinanceVoucher) => (
          <Space size={4}>
            <StatusTag status={s} />
            {s === "pending_approval" && (
              <Tooltip title={`${row.approvals_count} of ${row.approvals_required} approvals`}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {row.approvals_count}/{row.approvals_required}
                </Text>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "",
        key: "actions",
        width: 220,
        render: (_: any, row: FinanceVoucher) => (
          <Space size={4} wrap>
            <Button size="small" onClick={() => navigate(`/finance/vouchers/${row.id}`)}>
              View
            </Button>
            {row.status === "draft" && canCreate && (
              <Popconfirm
                title="Submit for approval?"
                onConfirm={() =>
                  runAction("Submitted for approval", () => financeService.submitVoucher(row.id))
                }
              >
                <Button size="small" type="primary">
                  Submit
                </Button>
              </Popconfirm>
            )}
            {row.status === "pending_approval" && hasPermission("finance:approve") && (
              <Button
                size="small"
                onClick={() => navigate(`/finance/vouchers/${row.id}`)}
                title="Open to approve"
              >
                Review
              </Button>
            )}
            {row.status === "draft" && canCreate && (
              <Popconfirm
                title="Delete this draft?"
                onConfirm={() => runAction("Draft deleted", () => financeService.deleteVoucher(row.id))}
              >
                <Button size="small" danger>
                  Delete
                </Button>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [navigate, canCreate],
  );

  const tabItems = [
    canIncome && { key: "income", label: "Income" },
    canExpense && { key: "expense", label: "Expenses" },
  ].filter(Boolean) as Array<{ key: string; label: string }>;

  if (tabItems.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          showIcon
          title="No finance recording permission"
          description="Your account can view the ledger but not record income or expenses."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
        wrap
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Income &amp; Expenses
          </Title>
          <Text type="secondary">
            {data ? `${data.total} voucher(s)` : "Loading…"}
          </Text>
        </div>
        <Space wrap>
          {canCreate && (
            <Button type="primary" onClick={() => setFormOpen(true)}>
              New {activeType === "income" ? "income" : "expense"}
            </Button>
          )}
          <Button onClick={load}>Refresh</Button>
        </Space>
      </Space>

      {tabItems.length > 1 && (
        <Tabs
          activeKey={activeType}
          onChange={(key) => {
            setPage(1);
            setRequested(key as VoucherType);
            // Keep the URL in sync so the sidebar highlight follows the active tab
            // (replace: true avoids spamming browser history on tab switches).
            setSearchParams({ type: key }, { replace: true });
          }}
          items={tabItems}
          style={{ marginBottom: 8 }}
        />
      )}

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Any status"
          style={{ width: 190 }}
          value={status}
          onChange={(v) => {
            setPage(1);
            setStatus(v);
          }}
          options={Object.entries(STATUS_TAG)
            .filter(([k]) => k !== "void" || activeType === "income")
            .map(([value, meta]) => ({ value, label: meta.label }))}
        />
        <Select
          allowClear
          placeholder="Any department"
          style={{ width: 200 }}
          value={departmentId}
          onChange={(v) => {
            setPage(1);
            setDepartmentId(v);
          }}
          options={departments.map((d) => ({ value: d.id, label: d.name }))}
        />
        <RangePicker
          value={range}
          onChange={(dates) => {
            setPage(1);
            setRange(dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null);
          }}
        />
        <Input.Search
          allowClear
          placeholder="Search description / voucher / party"
          style={{ width: 280 }}
          onSearch={(value) => {
            setPage(1);
            setQ(value);
          }}
        />
      </Space>

      {error && (
        <Alert type="error" showIcon title="Failed to load" description={error} style={{ marginBottom: 16 }} />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.rows ?? []}
        loading={loading}
        scroll={{ x: 1180 }}
        size="small"
        pagination={{
          current: page,
          pageSize: 20,
          total: data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
          showTotal: (t) => `${t} vouchers`,
        }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${activeType} vouchers yet`} />
          ),
        }}
      />

      <VoucherFormModal
        open={formOpen}
        initialType={activeType}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          message.success("Voucher created");
          load();
        }}
      />
    </div>
  );
};

export default VouchersPage;
